import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api.js';

/**
 * The signed-in user as returned by `GET /api/v1/me` (core-identity). Typed structurally here so the
 * SDK does not depend on a module; the shell guarantees the query is loaded before app pages render.
 */
export interface CurrentUser {
  user: {
    id: string;
    email: string;
    name: string;
    image: string | null;
    status: string;
    isSystemAdmin: boolean;
    isEmergencyAccount: boolean;
    twoFactorEnabled: boolean;
    locale: string;
    timezone: string;
  };
  tenant: { id: string; name: string };
  /** Error code when the account may only reach its own pages (pending approval, 2FA required). */
  restriction: string | null;
  permissions: string[];
}

export const meQueryKey = ['me'] as const;
export const fetchMe = () => apiFetch<CurrentUser>('me');

export function useMe() {
  return useQuery({ queryKey: meQueryKey, queryFn: fetchMe, staleTime: 60_000 });
}

/** Whether the current user holds a permission (`<module>.<resource>.<action>`). */
export function useCan(permission: string): boolean {
  const { data } = useMe();
  return data?.permissions.includes(permission) ?? false;
}

export async function signOut(): Promise<void> {
  await fetch('/api/auth/sign-out', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: '{}',
  });
}

/** Clears every cached query and reloads the session (after sign-in/out or account changes). */
export function useResetSession() {
  const client = useQueryClient();
  return () => client.resetQueries();
}
